import React, { useRef, useState, useEffect, useCallback } from "react";

/**
 * Canvas-based crop region selector.
 * Uses an <img> for display and an overlay canvas for crop drawing.
 * This avoids CORS issues with Azure Blob SAS URLs.
 * Returns { x, y, w, h } as fractions (0.0-1.0) of image dimensions.
 */
function CropDrawer({ imageUrl, initialCrop, onChange }) {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const imgElRef = useRef(null);
    const [imgLoaded, setImgLoaded] = useState(false);
    const [crop, setCrop] = useState(initialCrop || null);
    const [drawing, setDrawing] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [resizing, setResizing] = useState(null); // corner handle being dragged

    // Sync initial crop
    useEffect(() => {
        setCrop(initialCrop || null);
    }, [initialCrop]);

    // Reset loaded state when URL changes
    useEffect(() => {
        setImgLoaded(false);
    }, [imageUrl]);

    // Get position from mouse or touch event, as fractions of the canvas
    const getRelativePos = useCallback((e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        // Support both mouse and touch
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        const x = (clientX - rect.left) / rect.width;
        const y = (clientY - rect.top) / rect.height;
        return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
    }, []);

    // Check if point is near a corner handle
    const getHandle = useCallback((pos) => {
        if (!crop) return null;
        const threshold = 0.03; // 3% of image size
        const corners = [
            { name: "tl", x: crop.x, y: crop.y },
            { name: "tr", x: crop.x + crop.w, y: crop.y },
            { name: "bl", x: crop.x, y: crop.y + crop.h },
            { name: "br", x: crop.x + crop.w, y: crop.y + crop.h },
        ];
        for (const c of corners) {
            if (Math.abs(pos.x - c.x) < threshold && Math.abs(pos.y - c.y) < threshold) {
                return c.name;
            }
        }
        return null;
    }, [crop]);

    // Sync canvas size to image element and draw crop overlay
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const imgEl = imgElRef.current;
        if (!canvas || !imgEl || !imgLoaded) return;

        const w = imgEl.clientWidth;
        const h = imgEl.clientHeight;
        if (w === 0 || h === 0) return;

        canvas.width = w;
        canvas.height = h;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, w, h);

        if (crop) {
            // Dim everything outside the crop
            ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
            // Top
            ctx.fillRect(0, 0, w, crop.y * h);
            // Bottom
            ctx.fillRect(0, (crop.y + crop.h) * h, w, h - (crop.y + crop.h) * h);
            // Left
            ctx.fillRect(0, crop.y * h, crop.x * w, crop.h * h);
            // Right
            ctx.fillRect((crop.x + crop.w) * w, crop.y * h, w - (crop.x + crop.w) * w, crop.h * h);

            // Draw crop border
            ctx.strokeStyle = "#3b82f6";
            ctx.lineWidth = 2;
            ctx.strokeRect(crop.x * w, crop.y * h, crop.w * w, crop.h * h);

            // Draw corner handles — larger on touch devices
            const handleSize = 12;
            ctx.fillStyle = "#3b82f6";
            const corners = [
                [crop.x * w, crop.y * h],
                [(crop.x + crop.w) * w, crop.y * h],
                [crop.x * w, (crop.y + crop.h) * h],
                [(crop.x + crop.w) * w, (crop.y + crop.h) * h],
            ];
            for (const [cx, cy] of corners) {
                ctx.beginPath();
                ctx.arc(cx, cy, handleSize / 2, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    }, [crop, imgLoaded]);

    useEffect(() => { draw(); }, [draw]);

    // Pointer handlers (work for both mouse and touch)
    const handlePointerDown = (e) => {
        e.preventDefault();
        const pos = getRelativePos(e);
        if (!pos) return;

        // Check for corner handle resize
        const handle = getHandle(pos);
        if (handle) {
            setResizing(handle);
            return;
        }

        // Start new crop draw
        setDrawing(true);
        setDragStart(pos);
        setCrop(null);
    };

    const handlePointerMove = (e) => {
        e.preventDefault();
        const pos = getRelativePos(e);
        if (!pos) return;

        if (resizing && crop) {
            let newCrop = { ...crop };
            if (resizing === "tl") {
                newCrop = { x: pos.x, y: pos.y, w: crop.x + crop.w - pos.x, h: crop.y + crop.h - pos.y };
            } else if (resizing === "tr") {
                newCrop = { x: crop.x, y: pos.y, w: pos.x - crop.x, h: crop.y + crop.h - pos.y };
            } else if (resizing === "bl") {
                newCrop = { x: pos.x, y: crop.y, w: crop.x + crop.w - pos.x, h: pos.y - crop.y };
            } else if (resizing === "br") {
                newCrop = { x: crop.x, y: crop.y, w: pos.x - crop.x, h: pos.y - crop.y };
            }
            // Ensure positive dimensions
            if (newCrop.w > 0.02 && newCrop.h > 0.02) {
                setCrop(newCrop);
            }
            return;
        }

        if (drawing && dragStart) {
            const x = Math.min(dragStart.x, pos.x);
            const y = Math.min(dragStart.y, pos.y);
            const w = Math.abs(pos.x - dragStart.x);
            const h = Math.abs(pos.y - dragStart.y);
            if (w > 0.01 && h > 0.01) {
                setCrop({ x, y, w, h });
            }
        }
    };

    const handlePointerUp = () => {
        if (drawing || resizing) {
            setDrawing(false);
            setResizing(null);
            setDragStart(null);
            if (crop && onChange) {
                onChange({
                    x: Math.round(crop.x * 100) / 100,
                    y: Math.round(crop.y * 100) / 100,
                    w: Math.round(crop.w * 100) / 100,
                    h: Math.round(crop.h * 100) / 100,
                });
            }
        }
    };

    const clearCrop = () => {
        setCrop(null);
        if (onChange) onChange(null);
    };

    if (!imageUrl) {
        return <div className="crop-drawer-empty">No image available</div>;
    }

    return (
        <div className="crop-drawer" ref={containerRef}>
            <div className="crop-drawer-canvas-wrap">
                {/* Real <img> for display — no CORS issues */}
                <img
                    ref={imgElRef}
                    className="crop-drawer-img"
                    src={imageUrl}
                    alt="Preset"
                    onLoad={() => { setImgLoaded(true); setTimeout(draw, 50); }}
                    draggable={false}
                />
                {/* Transparent canvas overlay for crop drawing */}
                {imgLoaded && (
                    <canvas
                        ref={canvasRef}
                        className="crop-drawer-canvas"
                        onMouseDown={handlePointerDown}
                        onMouseMove={handlePointerMove}
                        onMouseUp={handlePointerUp}
                        onMouseLeave={handlePointerUp}
                        onTouchStart={handlePointerDown}
                        onTouchMove={handlePointerMove}
                        onTouchEnd={handlePointerUp}
                    />
                )}
            </div>
            <div className="crop-drawer-controls">
                {crop ? (
                    <>
                        <span className="crop-drawer-info">
                            Crop: {Math.round(crop.x * 100)}%, {Math.round(crop.y * 100)}%
                            {" "}{Math.round(crop.w * 100)}%x{Math.round(crop.h * 100)}%
                        </span>
                        <button className="crop-drawer-clear-btn" onClick={clearCrop}>
                            Clear crop
                        </button>
                    </>
                ) : (
                    <span className="crop-drawer-hint">
                        Click and drag to draw a crop region
                    </span>
                )}
            </div>
        </div>
    );
}

export default CropDrawer;
